import React, { InputHTMLAttributes, LabelHTMLAttributes } from "react"
import * as styles from "./Checkbox.module.scss"

export type CheckboxProps = {
  name?: string
  label: React.ReactNode
  /** Unique value when form requires multiple inputs with same `name`. */
  id?: string
  value?: string
  required?: boolean
  /**
   * Added to label if `required` is `false` e.g. field is optional
   * @default optional
   */
  optionalText?: string
  /** Error message describing why value is not passing validation. Implies input is in error state. */
  errorMsg?: string
  inputProps?: InputHTMLAttributes<HTMLInputElement>
  labelProps?: LabelHTMLAttributes<HTMLLabelElement>
}

/**
 * A checkbox that
 * - scales with (inherited) `font-size` of label
 * - can be colored or uses the same color as label for consistent theme/design instead of browser styles
 * - consistent, cross-browser design style, including :focus state
 * - maintain accessibility of native implementation, including keyboard usability
 */
const Checkbox: React.FC<CheckboxProps> = props => {
  // console.debug("Rendering Checkbox", props)
  // const uid = useUID()
  const { required = true, optionalText = "optional" } = props
  const id = props.id || props.name
  const errorId = `${id}-err`
  return (
    <div className={styles.container}>
      <label {...props.labelProps} className={styles.checkbox}>
        <span className={styles.wrapper}>
          <input
            className={styles.input}
            type="checkbox"
            name={props.name}
            value={props.value || "1"}
            required={props.required}
            aria-describedby={errorId}
            {...props.inputProps}
          />
          <span className={styles.box}>
            <svg viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg">
              <rect width="1em" height="1em" />
            </svg>
          </span>
        </span>
        <span>
          {props.label} {required === false ? <span className={styles.optional}>{optionalText}</span> : ""}
        </span>
      </label>
      <label className={styles.error} id={errorId}>
        {props.errorMsg}
      </label>
    </div>
  )
}

export default Checkbox
